import React from 'react';
import { EstateProfile } from '../../types';
import { Edit2 } from 'lucide-react';

interface Props {
    profile: EstateProfile;
    goToStep?: (stepId: string) => boolean;
}

export const InputsUsedPanel: React.FC<Props> = ({ profile, goToStep }) => {
    const d = profile.deceased || {};

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return 'Unknown';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        } catch (e) {
            return dateStr;
        }
    };

    const getDomicileLabel = (val: string | undefined) => {
        if (val === 'UK') return 'United Kingdom';
        if (val === 'NON_UK') return 'Non-UK';
        if (val === 'UNSURE') return 'Unsure (Pending)';
        return 'Unknown';
    };

    const getMaritalLabel = (val: string | undefined) => {
        switch (val) {
            case 'MARRIED_OR_CP': return 'Married / Civil Partnership';
            case 'WIDOWED': return 'Widowed';
            case 'DIVORCED_OR_SEPARATED': return 'Divorced / Separated';
            case 'NEVER_MARRIED': return 'Never Married';
            default: return 'Unknown';
        }
    };

    const handleEdit = () => {
        if (goToStep) goToStep('basics');
    };

    interface Entry {
        label: string;
        value: string;
        caption: string;
        muted?: boolean;
    }

    const entries: Entry[] = [
        {
            label: 'Date of Death',
            value: formatDate(d.date_of_death),
            caption: 'Determines tax year & rates.',
        },
        {
            label: 'Domicile',
            value: getDomicileLabel(d.domicile),
            caption: 'Affects IHT scope & allowances.',
            muted: d.domicile === 'UNSURE',
        },
        {
            label: 'Status at Death',
            value: getMaritalLabel(d.maritalStatus),
            caption: 'Affects spouse exemption.',
        },
    ];

    return (
        <div className="bg-white border border-[#E8E4DB] mb-6">
            <div className="px-5 py-3 border-b border-[#E8E4DB] flex justify-between items-center">
                <h4 className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] m-0">
                    Key details used
                </h4>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                {entries.map((entry, idx) => (
                    <div key={idx} className="relative group">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-1">{entry.label}</p>
                                <p className={`font-mono tabular-nums text-[13px] ${entry.muted ? 'text-[#7A5A00] italic' : 'text-[#1A1A1A]'}`}>
                                    {entry.value}
                                </p>
                                <p className="text-[11px] text-[#AFA99F] italic mt-1 leading-snug">{entry.caption}</p>
                            </div>
                            {goToStep && (
                                <button
                                    onClick={handleEdit}
                                    className="text-[#AFA99F] hover:text-[#1A1A1A] transition-colors p-1"
                                    title="Edit"
                                >
                                    <Edit2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="px-5 py-2 border-t border-[#E8E4DB] text-[11px] text-[#6B6762] italic">
                If any of these details are incorrect, your IHT route or estimate may change.
            </div>
        </div>
    );
};
